import React from 'react';

import {
  ErrorView as Error,
  ISO_DATE_FORMAT,
  NoPermissions,
  NotFound,
  rolesChildren,
} from '@gasbi/shared';
import { VerifiedFieldLabel } from '@gasbi/shared/common/components';
import { OrganizationPages } from '@gasbi/shared/web/Organization/pages';
import {
  EmployeeDetails,
  MyOrganization,
  MyOrganizationBankDetails,
  MyOrganizationEmployees,
  MyOrganizationInformation,
} from '@gasbi/shared/web/RbacOrganization';
import dayjs from 'dayjs';
import { Navigate, Outlet, createBrowserRouter } from 'react-router-dom';

import { makeAuditRoute } from '@/components/Audit/utils';
import { UserPermissions } from '@/features/permissions';
import { Pages } from '@/pages';
import allocationActualRoute from '@/pages/AllocationActual/route';
import allocationPreliminaryRoute from '@/pages/AllocationPreliminary/route';
import { contractDetailsRoutes } from '@/pages/ContractDetails/routes';
import counterpartyRoute from '@/pages/CounterpartyDetails/route';
import imbalancesRoute from '@/pages/Imbalances/route';
import { meteringPointDetailsRoutes } from '@/pages/MeteringPointDetails/routes';
import nominationsRoute from '@/pages/Nominations/route';
import resourceRoute from '@/pages/Resource/route';
import resourceCoverageRoute from '@/pages/ResourceCoverage/route';
import supplierPortfolioHistoryRoute from '@/pages/SupplierPortfolioHistory/route';
import {
  OrganizationApiContextWrapper,
  SupplyOrganizationApiWrapper,
} from '@/providers';
import type { AppRouteObject } from '@/routeTypes';

const Router = React.lazy(() => import('@/layout/Router'));
const AuthLanding = React.lazy(() => import('@/pages/auth/AuthCallback'));
const NotAuthorizedPage = React.lazy(() => import('@/pages/auth/NotAuthorizedPage'));
const ChangePassword = React.lazy(() => import('@/pages/auth/ChangePassword'));
const Balance = React.lazy(() => import('@/pages/Balance'));
const CounterpartiesDashboard = React.lazy(
  () => import('@/pages/Counterparties/CounterpartiesDashboard'),
);
const Counterparties = React.lazy(() => import('@/pages/Counterparties'));
const Contracts = React.lazy(() => import('@/pages/Contracts'));
const ContractDetails = React.lazy(() => import('@/pages/ContractDetails'));
const ContractAddendumForm = React.lazy(
  () => import('@/pages/ContractAddendumForm'),
);
const ContractAddendumDetails = React.lazy(
  () => import('@/pages/ContractAddendumDetails'),
);
const ContractAudit = React.lazy(() => import('@/pages/ContractAudit'));
const ForecastVolumes = React.lazy(() => import('@/pages/ForecastVolumes'));
const ConsumptionForecasts = React.lazy(
  () => import('@/pages/ConsumptionForecasts'),
);
const ConsumptionObjects = React.lazy(
  () => import('@/pages/ConsumptionObjects'),
);
const MeteringPointDetails = React.lazy(
  () => import('@/pages/MeteringPointDetails'),
);
const ConsumptionObjectAudit = React.lazy(
  () => import('@/pages/ConsumptionObjectAudit'),
);

const FormTen = React.lazy(() => import('@/pages/FormTen'));
const Reports = React.lazy(() => import('@/pages/Reports'));
const ReportConstructor = React.lazy(() => import('@/pages/ReportConstructor'));
const BiDashboards = React.lazy(() => import('@/pages/BiDashboards'));
const InvoiceSettings = React.lazy(() => import('@/pages/InvoiceSettings'));
const PhysicoChemicalIndicatorSettings = React.lazy(
  () => import('@/pages/PhysicoChemicalIndicatorSettings'),
);
const SupplierPortfolio = React.lazy(() => import('@/pages/SupplierPortfolio'));
const SupplierPortfolioImportDetails = React.lazy(
  () => import('@/pages/SupplierPortfolioImportDetails'),
);

export const routes: AppRouteObject[] = [
  {
    path: OrganizationPages.ROLES_PART,
    element: (
      <OrganizationApiContextWrapper>
        <Outlet />
      </OrganizationApiContextWrapper>
    ),
    children: rolesChildren(false),
  },
  {
    element: (
      <SupplyOrganizationApiWrapper>
        <MyOrganization
          employeesPath={Pages.EMPLOYEES_PART}
          bankDetailsPath={Pages.BANK_DETAILS_PART}
        />
      </SupplyOrganizationApiWrapper>
    ),
    children: [
      {
        index: true,
        element: (
          <MyOrganizationInformation verifiedBadge={VerifiedFieldLabel} />
        ),
        handle: {
          permissions: UserPermissions.OrganizationsOrganizationRead,
        },
      },
      {
        path: Pages.EMPLOYEES_PART,
        element: <MyOrganizationEmployees />,
      },
      {
        path: Pages.BANK_DETAILS_PART,
        element: <MyOrganizationBankDetails />,
      },
    ],
  },
  {
    path: `${Pages.EMPLOYEES_PART}/:employeeId`,
    element: (
      <SupplyOrganizationApiWrapper>
        <EmployeeDetails />
      </SupplyOrganizationApiWrapper>
    ),
  },
  {
    path: Pages.CHANGE_PASSWORD,
    element: <ChangePassword />,
  },
  {
    path: Pages.BALANCE,
    element: <Balance />,
  },
  ...resourceRoute,
  ...supplierPortfolioHistoryRoute,
  ...imbalancesRoute,
  ...resourceCoverageRoute,
  ...nominationsRoute,
  ...allocationActualRoute,
  ...allocationPreliminaryRoute,
  {
    path: Pages.FORECAST_VOLUMES,
    element: (
      <Navigate
        to={`./${dayjs().format(ISO_DATE_FORMAT)}`}
        relative='path'
        replace
      />
    ),
  },
  {
    path: `${Pages.FORECAST_VOLUMES}/${Pages.CONSUMPTION_FORECASTS}`,
    element: <ConsumptionForecasts />,
  },
  {
    path: `${Pages.FORECAST_VOLUMES}/:date`,
    element: <ForecastVolumes />,
  },
  {
    path: Pages.COUNTERPARTIES_DASHBOARD,
    element: <CounterpartiesDashboard />,
  },
  {
    path: Pages.COUNTERPARTIES,
    element: <Navigate to='all' replace />,
  },
  {
    path: `${Pages.COUNTERPARTIES}/:category`,
    element: <Counterparties />,
  },
  ...counterpartyRoute,
  makeAuditRoute(
    `${Pages.COUNTERPARTIES}/:category/:counterpartId/${Pages.CONTRACT_DETAILS}`,
    <ContractAudit />,
    UserPermissions.OrganizationsContractsRead,
  ),
  {
    path: Pages.CONTRACTS,
    element: <Contracts />,
  },
  {
    path: Pages.CONTRACT_DETAILS,
    element: <ContractDetails />,
    children: contractDetailsRoutes,
  },
  {
    path: `${Pages.CONTRACT_DETAILS}/${Pages.CONTRACT_ADDENDA_CREATE_PART}`,
    element: <ContractAddendumForm />,
    handle: { permissions: UserPermissions.OrganizationsContractsWrite },
  },
  {
    path: `${Pages.CONTRACT_DETAILS}/${Pages.CONTRACT_ADDENDUM_DETAILS_PART}`,
    element: <ContractAddendumDetails />,
  },
  makeAuditRoute(
    Pages.CONTRACT_DETAILS,
    <ContractAudit />,
    UserPermissions.OrganizationsContractsRead,
  ),
  {
    path: Pages.CONSUMPTION_OBJECTS,
    element: <ConsumptionObjects />,
  },
  {
    path: Pages.METERING_POINT_DETAILS,
    element: <MeteringPointDetails />,
    children: meteringPointDetailsRoutes,
  },
  {
    path: `${Pages.CONTRACT_DETAILS}/${Pages.CONSUMPTION_OBJECTS}/:meteringPointId`,
    element: <MeteringPointDetails />,
    children: meteringPointDetailsRoutes,
  },
  {
    path: `${Pages.COUNTERPARTIES}/:category/:counterpartyId/${Pages.CONTRACTS}/:contractId/${Pages.CONSUMPTION_OBJECTS}/:meteringPointId`,
    element: <MeteringPointDetails />,
    children: meteringPointDetailsRoutes,
  },
  makeAuditRoute(
    Pages.METERING_POINT_DETAILS,
    <ConsumptionObjectAudit />,
    UserPermissions.OrganizationsConsumptionObjectsRead,
  ),
  makeAuditRoute(
    `${Pages.CONTRACT_DETAILS}/${Pages.CONSUMPTION_OBJECTS}/:meteringPointId`,
    <ConsumptionObjectAudit />,
    UserPermissions.OrganizationsConsumptionObjectsRead,
  ),
  {
    path: Pages.FORM_TEN,
    element: <Navigate to={dayjs().format(ISO_DATE_FORMAT)} replace />,
    handle: {
      permissions: UserPermissions.OrganizationsLastResortSupplierRead,
    },
  },
  {
    path: `${Pages.FORM_TEN}/:date`,
    element: <FormTen />,
    handle: {
      permissions: UserPermissions.OrganizationsLastResortSupplierRead,
    },
  },
  {
    path: `${Pages.REPORTS_LIST}`,
    element: <Navigate to='all' replace />,
    handle: {
      permissions: UserPermissions.ReportsAndAnalyticsReportRead,
    },
  },
  {
    path: `${Pages.REPORTS_LIST}/:reportTab`,
    element: <Reports />,
    handle: {
      permissions: UserPermissions.ReportsAndAnalyticsReportRead,
    },
  },
  {
    path: Pages.BI_DASHBOARDS,
    element: <BiDashboards />,
    handle: {
      permissions: UserPermissions.ReportsAndAnalyticsBiDashboardsRead,
    },
  },
  {
    path: `${Pages.REPORTS}/constructor`,
    element: <ReportConstructor />,
  },
  {
    path: `${Pages.REPORTS}/dev-extreme`,
    element: <ReportConstructor isDevExtreme />,
  },
  {
    path: `${Pages.REPORTS}/constructor/:reportTemplateId`,
    element: <ReportConstructor />,
  },
  {
    path: Pages.INVOICE_SETTINGS,
    element: <InvoiceSettings />,
    handle: {
      permissions: UserPermissions.SettingsInvoiceSettingsRead,
    },
  },
  {
    path: Pages.PHYSICO_CHEMICAL_INDICATOR_SETTINGS,
    element: <PhysicoChemicalIndicatorSettings />,
    handle: {
      permissions: UserPermissions.SettingsPhysicoChemicalIndicatorsRead,
    },
  },
  {
    path: Pages.SUPPLIER_PORTFOLIO,
    element: <SupplierPortfolio />,
    handle: {
      permissions: UserPermissions.OrganizationsSupplierPortfolioRead,
    },
  },
  {
    path: Pages.SUPPLIER_PORTFOLIO_IMPORT_DETAILS,
    element: <SupplierPortfolioImportDetails />,
    handle: {
      permissions: UserPermissions.OrganizationsSupplierPortfolioRead,
    },
  },
  { path: '*', element: <NotFound /> },
  { path: Pages.NOT_FOUND, element: <NotFound /> },
];

export const allRoutes = [
  {
    path: '/',
    element: <AuthLanding />,
  },
  {
    path: Pages.AUTH_CALLBACK,
    element: <AuthLanding />,
  },
  {
    path: Pages.NO_PERMISSIONS,
    element: <NoPermissions />,
  },
  {
    path: Pages.NOT_AUTHORIZED,
    element: <NotAuthorizedPage />,
  },
  {
    path: '/:contextOrganizationId/*',
    element: <Router />,
    children: routes,
  },
  { path: '*', element: <NotFound /> },
  { path: Pages.NOT_FOUND, element: <NotFound /> },
];

export const getRoutes = () =>
  createBrowserRouter(
    allRoutes.map(route => ({ ...route, errorElement: <Error /> })),
  );
